import { View } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractGroupItem from '../../item/abstractGroupItem';

/**
 * This view displays dynamic collisions
 */
export default class DynamicCollisionView extends View {
    containerItem = null;
    layer = null;
    selection = null;
    pathFactory = null;
    svg = null;
    group = null;
    itemGroup = null;
    innerItemGroups = new Map();
    outerItemGroups = new Map();
    innerClipPaths = new Map();
    outerClipPaths = new Map();
    currentItemPaths = new Map();

    constructor(containerItem, layer, selection, pathFactory) {
        super();

        this.containerItem = containerItem;
        this.layer = layer;
        this.selection = selection;
        this.pathFactory = pathFactory;
        this.svg = this.layer.getSvg();

        this.selection.on('selectionchange', this._onSelectionChange, this);
        containerItem.on('change', this._onItemChange, this);
        containerItem.getItems().on('change', this._onItemChange, this);

        this.group = this.svg.group(document.createDocumentFragment());
        this.group.setAttribute('class', 'dynamic-collisions');

        this.itemGroup = this.svg.group(this.group);
    }

    _onSelectionChange() {
        if (this.selection.length > 0) this._updateCollisions();
        else this._clearView();
    }

    _onItemChange(item) {
        this._breakDownGroupItems([item]).forEach(subItem => this._updateItemPath(subItem));
    }

    _clearView() {
        while (this.itemGroup.firstChild) {
            this.itemGroup.removeChild(this.itemGroup.firstChild);
        }

        this.innerItemGroups.clear();
        this.innerClipPaths.clear();
        this.outerItemGroups.clear();
        this.outerClipPaths.clear();
        this.currentItemPaths.clear();
    }

    _updateCollisions() {
        const selectedItems = this._getSelectedItems();
        const items = this._getUnselectedItems();

        this._clearView();

        const selectedMargins = new Set();

        selectedItems.forEach((item) => {
            const margin = item.getSafetyMargin();
            selectedMargins.add(margin);
            const paths = {};

            if (item.supportsCollisions) {
                const collisionPaths = this._createSelectedItemPath(item, 'collision');
                paths.collisionInner = collisionPaths.innerPath;
                paths.collisionOuter = collisionPaths.outerPath;
            }

            if (item.supportsOutOfFoam) {
                const outOfContainerPaths = this._createSelectedItemPath(item, 'out-of-container');
                paths.outOfContainerInner = outOfContainerPaths.innerPath;
                paths.outOfContainerOuter = outOfContainerPaths.outerPath;
            }

            this.currentItemPaths.set(item, paths);
        });

        selectedMargins.forEach((margin) => {
            this._createItemClipPaths(items.filter(item => item.supportsCollisions), margin, 'collision');
            this._createItemClipPaths([this.containerItem], margin, 'out-of-container');
        });

        this.layer.getElement().append(this.group);
    }

    _createSelectedItemPath(item, type) {
        const margin = item.getSafetyMargin();

        const itemGroupInner = this._getInnerItemGroup(margin, type);
        const innerPath = this.pathFactory.createBagPath(itemGroupInner, item);

        const itemGroupOuter = this._getOuterItemGroup(margin, type);
        const outerPath = this.pathFactory.createMarginPath(itemGroupOuter, item);

        innerPath.setAttribute('transform', this.pathFactory.getTransformString(item));
        outerPath.setAttribute('transform', this.pathFactory.getTransformString(item));

        return { innerPath, outerPath };
    }

    _createItemClipPaths(items, margin, type) {
        const clipPathInner = this._getInnerClipPath(margin, type);
        const clipPathOuter = this._getOuterClipPath(margin, type);

        items.forEach((otherItem) => {
            if (this.currentItemPaths.has(otherItem)) return; // Item is in selection, don't collide with selection!

            const otherMargin = otherItem.getSafetyMargin();

            if (otherItem.supportsBidirectionalCollisions) {
                this._createMarginPath(clipPathInner, otherItem);
                this._createBagPath(clipPathOuter, otherItem);
            } else if (otherMargin > margin) {
                this._createMarginPath(clipPathInner, otherItem);
            } else {
                this._createBagPath(clipPathOuter, otherItem);
            }
        });

        // Workaround for Firefox bug [OFCS-954]
        // Firefox seems to fully show a path that has an empty clip path
        // Chrome hides a path with an empty clip path
        if (clipPathInner.childNodes.length === 0) this.svg.path(clipPathInner, 'M1,1');
        if (clipPathOuter.childNodes.length === 0) this.svg.path(clipPathOuter, 'M1,1');
    }

    _updateItemPath(item) {
        // Fully update collisions if a property changes which influences its margin
        if (item.hasChangedProperty('depth', 'toolVersion')) {
            this._updateCollisions();
            return;
        }

        const itemPaths = this.currentItemPaths.get(item);
        if (!itemPaths) return;

        const transformString = this.pathFactory.getTransformString(item);

        if (itemPaths) {
            if (item.hasChangedProperty('position', 'rotation', 'flipped')) {
                Object.values(itemPaths).forEach(path => path.setAttribute('transform', transformString));
            }

            if (item.hasChangedProperty('width', 'height', 'flipped')) {
                const factoryMethodMap = {
                    collisionInner: 'getBagPathString',
                    outOfContainerInner: 'getBagPathString',
                    collisionOuter: 'getMarginPathString',
                    outOfContainerOuter: 'getMarginPathString',
                };

                Object
                    .keys(itemPaths)
                    .forEach(key => itemPaths[key].setAttribute('d', this.pathFactory[factoryMethodMap[key]](item)));
            }
        }
    }

    _getInnerItemGroup(margin, type = 'collision') {
        const identifier = `${type}-${margin}`;
        if (!this.innerItemGroups.has(identifier)) {
            const clipPathId = `${type}-clip-inner-${margin}`;
            const group = this.svg.group(this.itemGroup, '', { 'clip-path': `url(#${clipPathId})` });
            this.innerItemGroups.set(identifier, group);
        }

        return this.innerItemGroups.get(identifier);
    }

    _getOuterItemGroup(margin, type = 'collision') {
        const identifier = `${type}-${margin}`;
        if (!this.outerItemGroups.has(identifier)) {
            const clipPathId = `${type}-clip-outer-${margin}`;
            const group = this.svg.group(this.itemGroup, '', { 'clip-path': `url(#${clipPathId})` });
            this.outerItemGroups.set(identifier, group);
        }

        return this.outerItemGroups.get(identifier);
    }

    _getInnerClipPath(margin, type = 'collision') {
        const identifier = `${type}-${margin}`;
        if (!this.innerClipPaths.has(identifier)) {
            const clipPathId = `${type}-clip-inner-${margin}`;
            const clipPath = this.svg.clipPath(this.itemGroup, clipPathId);
            this.innerClipPaths.set(identifier, clipPath);
        }

        return this.innerClipPaths.get(identifier);
    }

    _getOuterClipPath(margin, type = 'collision') {
        const identifier = `${type}-${margin}`;
        if (!this.outerClipPaths.has(identifier)) {
            const clipPathId = `${type}-clip-outer-${margin}`;
            const clipPath = this.svg.clipPath(this.itemGroup, clipPathId);
            this.outerClipPaths.set(identifier, clipPath);
        }

        return this.outerClipPaths.get(identifier);
    }

    _getAllSupportedItems() {
        return this
            ._breakDownGroupItems(this.containerItem.getItems().toArray())
            .concat([this.containerItem])
            .filter(item => (item.supportsCollisions || item.supportsOutOfFoam));
    }

    _getUnselectedItems() {
        const selectedItems = this.selection.toArray();

        return this
            ._getAllSupportedItems()
            .filter(item => selectedItems.indexOf(item) < 0);
    }

    _getSelectedItems() {
        const selectedItems = this.selection.toArray();

        return this
            ._getAllSupportedItems()
            .filter(item => selectedItems.indexOf(item) >= 0 && item !== this.containerItem);
    }

    _breakDownGroupItems(items) {
        return items.reduce((flattened, item) => {
            if (item instanceof AbstractGroupItem) {
                return flattened.concat(this._breakDownGroupItems(item.getItems()));
            }

            return flattened.concat([item]);
        }, []);
    }

    _createMarginPath(parent, item) {
        if (item === this.containerItem) {
            return this.pathFactory.createInnerCollisonPath(parent, item);
        }

        const path = this.pathFactory.createMarginPath(parent, item);
        path.setAttribute('transform', this.pathFactory.getTransformString(item));
        return path;
    }

    _createBagPath(parent, item) {
        if (item === this.containerItem) {
            return this.pathFactory.createOuterCollisonPath(parent, item);
        }

        const path = this.pathFactory.createBagPath(parent, item);
        path.setAttribute('transform', this.pathFactory.getTransformString(item));
        return path;
    }
}
